import { useEffect, useRef, useState } from 'react'
import { Ic } from '../../../components/ui'
import { DK } from '../theme'

/* ── Icon color presets for SectionTitle ── */
const IC_PALETTE = {
  user:   { bg:'linear-gradient(135deg,rgba(31,200,214,.22),rgba(10,123,134,.12))',  border:'rgba(31,200,214,.25)',  color:'#1FC8D6' },
  folder: { bg:'linear-gradient(135deg,rgba(99,102,241,.22),rgba(67,56,202,.12))',   border:'rgba(99,102,241,.28)',  color:'#818cf8' },
  cog:    { bg:'linear-gradient(135deg,rgba(251,146,60,.22),rgba(234,88,12,.12))',    border:'rgba(251,146,60,.28)',  color:'#fb923c' },
  info:   { bg:'linear-gradient(135deg,rgba(59,130,246,.22),rgba(37,99,235,.12))',    border:'rgba(59,130,246,.28)',  color:'#60a5fa' },
  chat:   { bg:'linear-gradient(135deg,rgba(34,197,94,.22),rgba(22,163,74,.12))',     border:'rgba(34,197,94,.28)',   color:'#4ade80' },
}

/* ── Card ── */
export function Card({ children, style, c, ...rest }) {
  const dk = c === DK
  return (
    <div style={{
      background: dk
        ? 'linear-gradient(145deg,rgba(7,20,34,.95) 0%,rgba(4,13,24,1) 100%)'
        : c.card,
      border: `1px solid ${c.line}`,
      borderRadius: 18,
      boxShadow: dk
        ? 'inset 0 1px 0 rgba(255,255,255,.05), inset 0 -1px 0 rgba(0,0,0,.15)'
        : '0 2px 16px rgba(10,80,100,.08), 0 1px 4px rgba(10,32,40,.05)',
      ...style,
    }} {...rest}>
      {children}
    </div>
  )
}

/* ── SectionTitle ── */
export function SectionTitle({ icon, label, badge, c }) {
  const ic = IC_PALETTE[icon] || IC_PALETTE.user
  const dk = c === DK
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, padding:'16px 20px', position:'relative' }}>
      <div style={{
        width:34, height:34, borderRadius:10, flexShrink:0,
        background: ic.bg,
        border:`1px solid ${ic.border}`,
        display:'flex', alignItems:'center', justifyContent:'center',
        color: ic.color,
        boxShadow:`0 2px 10px ${ic.color}22`,
      }}>
        <Ic n={icon} s={15}/>
      </div>
      <span style={{ fontSize:15, fontWeight:800, color:c.white, flex:1, letterSpacing:'-.015em' }}>{label}</span>
      {badge != null && (
        <span style={{
          fontSize:11, fontWeight:700, padding:'2px 9px', borderRadius:999,
          background: dk ? 'rgba(15,163,177,.12)' : 'rgba(15,163,177,.1)',
          border:`1px solid rgba(15,163,177,.22)`,
          color:c.teal, fontFamily:"'JetBrains Mono',monospace",
        }}>{badge}</span>
      )}
      <div style={{
        position:'absolute', bottom:0, left:20, right:20, height:1,
        background: dk
          ? `linear-gradient(90deg,rgba(31,200,214,.35),rgba(31,200,214,.08) 60%,transparent)`
          : `linear-gradient(90deg,rgba(10,123,134,.25),rgba(10,123,134,.06) 60%,transparent)`,
      }}/>
    </div>
  )
}

/* ── Toggle ── */
export function Toggle({ on, set, c }) {
  return (
    <div onClick={() => set(!on)} style={{
      width:40, height:22, borderRadius:11,
      background: on ? c.teal : c.bg3,
      position:'relative', cursor:'pointer', transition:'background .2s', flexShrink:0,
      border:`1px solid ${on ? c.teal : c.line}`,
    }}>
      <div style={{
        position:'absolute', top:2, left: on ? 20 : 2,
        width:16, height:16, borderRadius:'50%',
        background: on ? '#fff' : c.s3,
        transition:'left .2s', boxShadow:'0 1px 3px rgba(0,0,0,.25)',
      }}/>
    </div>
  )
}

/* ── Input ── */
export function Input({ value, onChange, placeholder, type = 'text', disabled, c }) {
  const [focus, setFocus] = useState(false)
  return (
    <input
      type={type} value={value} onChange={onChange} placeholder={placeholder} disabled={disabled}
      style={{
        width:'100%', maxWidth:'100%', boxSizing:'border-box', padding:'10px 14px',
        background:c.input, border:`1px solid ${focus ? c.teal : c.inputBorder}`,
        borderRadius:10, color:c.text,
        fontFamily:"'Plus Jakarta Sans','Manrope',system-ui,sans-serif", fontSize:16,
        outline:'none', transition:'border-color .2s',
        opacity: disabled ? .5 : 1,
        colorScheme: c === DK ? 'dark' : 'light',
      }}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
    />
  )
}

/* ── Select ── */
export function Select({ value, onChange, options, placeholder, disabled, c }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef(null)

  useEffect(() => {
    if (!open) { setSearch(''); return }
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const filtered = search
    ? options.filter(o => o.toLowerCase().includes(search.toLowerCase()))
    : options

  const select = (o) => { onChange({ target: { value: o } }); setOpen(false) }
  const showSearch = options.length > 8

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button type="button" disabled={disabled} onClick={() => !disabled && setOpen(o => !o)} style={{
        width:'100%', padding:'10px 36px 10px 14px',
        background:c.input,
        border:`1px solid ${open ? c.teal : c.inputBorder}`,
        borderRadius: open ? '10px 10px 0 0' : 10,
        color: value ? c.text : c.muted,
        fontFamily:"'Plus Jakarta Sans','Manrope',system-ui,sans-serif", fontSize:14,
        outline:'none', transition:'border-color .2s, border-radius .1s',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? .5 : 1,
        textAlign:'left', display:'block',
        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
      }}>
        {value || placeholder}
      </button>
      <div style={{ position:'absolute', right:12, top:11, pointerEvents:'none', color:c.muted, transition:'transform .2s', transform: open ? 'rotate(180deg)' : 'none' }}>
        <Ic n="chevdown" s={14}/>
      </div>
      {open && (
        <div style={{
          position:'absolute', top:'100%', left:0, right:0, zIndex:200,
          background: c === DK ? '#071422' : '#fff',
          border:`1px solid ${c.teal}`,
          borderTop:`1px solid ${c === DK ? '#122840' : 'rgba(10,32,40,.1)'}`,
          borderRadius:'0 0 10px 10px',
          boxShadow: c === DK ? '0 12px 32px rgba(0,0,0,.55)' : '0 8px 24px rgba(10,32,40,.14)',
          maxHeight:260, display:'flex', flexDirection:'column',
        }}>
          {showSearch && (
            <div style={{ padding:'8px 10px', borderBottom:`1px solid ${c.inputBorder}`, flexShrink:0 }}>
              <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Поиск..."
                style={{ width:'100%', padding:'6px 10px', background:c.input, border:`1px solid ${c.inputBorder}`, borderRadius:7, color:c.text, outline:'none', fontFamily:"'Plus Jakarta Sans','Manrope',sans-serif", fontSize:13, boxSizing:'border-box' }}/>
            </div>
          )}
          <div style={{ overflowY:'auto', flex:1 }}>
            {filtered.length === 0 ? (
              <div style={{ padding:'12px 14px', fontSize:13, color:c.muted, textAlign:'center' }}>Не найдено</div>
            ) : filtered.map(o => (
              <button key={o} type="button" onClick={() => select(o)} style={{
                display:'flex', alignItems:'center', justifyContent:'space-between',
                width:'100%', padding:'9px 14px', border:'none', textAlign:'left',
                background: o === value ? (c === DK ? 'rgba(15,163,177,.18)' : 'rgba(15,163,177,.1)') : 'transparent',
                color: o === value ? c.teal : c.text,
                fontFamily:"'Plus Jakarta Sans','Manrope',sans-serif", fontSize:13.5,
                cursor:'pointer', transition:'background .12s', fontWeight: o === value ? 700 : 400,
              }}
              onMouseEnter={e => { if (o !== value) e.currentTarget.style.background = c === DK ? 'rgba(255,255,255,.05)' : 'rgba(10,32,40,.05)' }}
              onMouseLeave={e => { if (o !== value) e.currentTarget.style.background = 'transparent' }}>
                {o}
                {o === value && <Ic n="check" s={14}/>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/* ── PwInput ── */
export function PwInput({ value, onChange, placeholder, c }) {
  const [show, setShow] = useState(false)
  const [focus, setFocus] = useState(false)
  return (
    <div style={{ position:'relative' }}>
      <input type={show ? 'text' : 'password'} value={value} onChange={onChange} placeholder={placeholder}
        style={{
          width:'100%', padding:'10px 42px 10px 14px',
          background:c.input, border:`1px solid ${focus ? c.teal : c.inputBorder}`,
          borderRadius:10, color:c.text,
          fontFamily:"'Plus Jakarta Sans','Manrope',system-ui,sans-serif", fontSize:14,
          outline:'none', transition:'border-color .2s',
        }}
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}/>
      <button type="button" onClick={() => setShow(s => !s)} style={{
        position:'absolute', right:12, top:'50%', transform:'translateY(-50%)',
        background:'none', border:'none', cursor:'pointer', color:c.muted, padding:0,
      }}>
        <Ic n={show ? 'eyeoff' : 'eye'} s={15}/>
      </button>
    </div>
  )
}

/* ── PoolThumb ── */
export function PoolThumb({ previewUrl, s = 120 }) {
  const [err, setErr] = useState(false)
  if (previewUrl && !err) {
    return (
      <img src={previewUrl} alt="pool" onError={() => setErr(true)}
        style={{ width:'100%', height:s, objectFit:'cover', display:'block' }}/>
    )
  }
  return (
    <div style={{
      width:'100%', height:s,
      background:'linear-gradient(145deg,#030B16 0%,#071422 55%,#0A1B2E 100%)',
      display:'flex', alignItems:'center', justifyContent:'center',
      position:'relative', overflow:'hidden',
    }}>
      <div style={{ position:'absolute', inset:0, backgroundImage:'linear-gradient(rgba(31,200,214,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(31,200,214,.05) 1px,transparent 1px)', backgroundSize:'20px 20px' }}/>
      <div style={{ position:'absolute', top:'15%', left:'50%', transform:'translateX(-50%)', width:'55%', height:'65%', background:'radial-gradient(circle,rgba(15,163,177,.14),transparent 70%)', pointerEvents:'none' }}/>
      <svg viewBox="0 0 80 50" style={{ width:'58%', opacity:.7, position:'relative', zIndex:1 }}>
        <path d="M40 2L78 22V42L40 48L2 42V22Z" fill="none" stroke="rgba(31,200,214,.35)" strokeWidth="1"/>
        <path d="M40 8L70 24V38L40 44L10 38V24Z" fill="rgba(15,163,177,.1)" stroke="#1FC8D6" strokeWidth=".8"/>
        <path d="M40 8L40 44M10 38L70 38M2 22L78 22" stroke="rgba(31,200,214,.18)" strokeWidth=".5"/>
        <circle cx="40" cy="26" r="4.5" fill="rgba(31,200,214,.12)" stroke="rgba(31,200,214,.5)" strokeWidth=".7"/>
        <circle cx="40" cy="26" r="1.5" fill="rgba(31,200,214,.6)"/>
      </svg>
    </div>
  )
}

/* ── Pool illustration (luxury aerial view) ── */
function PoolIllustration() {
  return (
    <svg viewBox="0 0 340 230" style={{ width:'100%', maxWidth:340, display:'block' }}>
      <defs>
        {/* Water — deep sapphire-to-aqua */}
        <radialGradient id="pi-w" cx="38%" cy="32%" r="72%">
          <stop offset="0%"   stopColor="#0D6E8A"/>
          <stop offset="40%"  stopColor="#095570"/>
          <stop offset="80%"  stopColor="#053C58"/>
          <stop offset="100%" stopColor="#022840"/>
        </radialGradient>
        {/* Additional depth shadow bottom-right */}
        <radialGradient id="pi-dep" cx="72%" cy="78%" r="55%">
          <stop offset="0%"   stopColor="rgba(0,10,25,.7)"/>
          <stop offset="100%" stopColor="rgba(0,10,25,0)"/>
        </radialGradient>
        {/* Surface shimmer top-left */}
        <radialGradient id="pi-hl" cx="22%" cy="20%" r="52%">
          <stop offset="0%"   stopColor="rgba(160,230,255,.3)"/>
          <stop offset="100%" stopColor="rgba(160,230,255,0)"/>
        </radialGradient>
        {/* Deck stone material */}
        <linearGradient id="pi-dk" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor="#1A2E3E"/>
          <stop offset="50%"  stopColor="#142433"/>
          <stop offset="100%" stopColor="#0E1B28"/>
        </linearGradient>
        {/* Deck subtle warm accent */}
        <radialGradient id="pi-dkhl" cx="80%" cy="20%" r="60%">
          <stop offset="0%"   stopColor="rgba(15,163,177,.06)"/>
          <stop offset="100%" stopColor="rgba(15,163,177,0)"/>
        </radialGradient>
        {/* Light rays through water (diagonal) */}
        <linearGradient id="pi-ray" x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0%"   stopColor="rgba(180,240,255,.18)"/>
          <stop offset="60%"  stopColor="rgba(100,200,240,.06)"/>
          <stop offset="100%" stopColor="rgba(100,200,240,0)"/>
        </linearGradient>
        {/* Diagonal surface shimmer band */}
        <linearGradient id="pi-sh" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor="rgba(255,255,255,0)"/>
          <stop offset="38%"  stopColor="rgba(255,255,255,.08)"/>
          <stop offset="50%"  stopColor="rgba(255,255,255,.16)"/>
          <stop offset="62%"  stopColor="rgba(255,255,255,.07)"/>
          <stop offset="100%" stopColor="rgba(255,255,255,0)"/>
        </linearGradient>
        {/* Float rope line */}
        <linearGradient id="pi-rl" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="rgba(255,255,255,.3)"/>
          <stop offset="100%" stopColor="rgba(255,255,255,.08)"/>
        </linearGradient>
        {/* Ambient glow */}
        <filter id="pi-gf" x="-45%" y="-45%" width="190%" height="190%">
          <feGaussianBlur stdDeviation="22"/>
        </filter>
        {/* Caustic soft blur */}
        <filter id="pi-cf" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="6"/>
        </filter>
        {/* Pool water clip */}
        <clipPath id="pi-c">
          <rect x="20" y="20" width="300" height="190" rx="13"/>
        </clipPath>
        {/* Deck outer clip */}
        <clipPath id="pi-dc">
          <rect x="3" y="3" width="334" height="224" rx="22"/>
        </clipPath>
      </defs>

      {/* ── Ambient glow behind illustration ── */}
      <ellipse cx="170" cy="118" rx="155" ry="95" fill="rgba(8,105,150,.28)" filter="url(#pi-gf)"/>

      {/* ── Pool deck ── */}
      <rect x="3" y="3" width="334" height="224" rx="22" fill="url(#pi-dk)"/>
      <rect x="3" y="3" width="334" height="224" rx="22" fill="url(#pi-dkhl)"/>

      {/* Deck edge glow line */}
      <rect x="3" y="3" width="334" height="224" rx="22"
        fill="none" stroke="rgba(15,163,177,.2)" strokeWidth="1.5"/>

      {/* Deck stone tile texture */}
      <g clipPath="url(#pi-dc)" opacity=".038">
        {[0,1,2,3,4,5,6,7,8,9,10,11].map(i => (
          <line key={`v${i}`} x1={3+i*30} y1="3" x2={3+i*30} y2="227" stroke="white" strokeWidth=".8"/>
        ))}
        {[0,1,2,3,4,5,6,7].map(i => (
          <line key={`h${i}`} x1="3" y1={3+i*29} x2="337" y2={3+i*29} stroke="white" strokeWidth=".8"/>
        ))}
      </g>

      {/* Pool rim highlight edge */}
      <rect x="11" y="11" width="318" height="208" rx="17"
        fill="none" stroke="rgba(100,185,215,.1)" strokeWidth="1" strokeDasharray="5 11"/>

      {/* ── Water body (3 gradient layers) ── */}
      <rect x="20" y="20" width="300" height="190" rx="13" fill="url(#pi-w)"/>
      <rect x="20" y="20" width="300" height="190" rx="13" fill="url(#pi-hl)"/>
      <rect x="20" y="20" width="300" height="190" rx="13" fill="url(#pi-dep)"/>

      {/* ── Underwater light shafts (diagonal beams) ── */}
      <g clipPath="url(#pi-c)" opacity=".9">
        <polygon points="52,20  80,20  148,210 120,210" fill="url(#pi-ray)"/>
        <polygon points="108,20 132,20 200,210 176,210" fill="url(#pi-ray)"/>
        <polygon points="178,20 198,20 265,210 245,210" fill="url(#pi-ray)"/>
        <polygon points="235,20 252,20 308,180 291,180" fill="url(#pi-ray)"/>
      </g>

      {/* ── Pool floor mosaic tile grid ── */}
      <g clipPath="url(#pi-c)" opacity=".055">
        {[0,1,2,3,4,5,6,7,8,9].map(i => (
          <line key={`vt${i}`} x1={20+i*33.3} y1="20" x2={20+i*33.3} y2="210" stroke="white" strokeWidth=".7"/>
        ))}
        {[0,1,2,3,4,5,6].map(i => (
          <line key={`ht${i}`} x1="20" y1={20+i*31.7} x2="320" y2={20+i*31.7} stroke="white" strokeWidth=".7"/>
        ))}
      </g>

      {/* ── Caustic light patches (organic shapes, blurred) ── */}
      <g clipPath="url(#pi-c)" filter="url(#pi-cf)">
        <ellipse cx="90"  cy="62"  rx="42" ry="24" fill="rgba(140,220,255,.14)" transform="rotate(-22,90,62)"/>
        <ellipse cx="215" cy="52"  rx="30" ry="18" fill="rgba(140,220,255,.12)" transform="rotate(14,215,52)"/>
        <ellipse cx="155" cy="110" rx="58" ry="30" fill="rgba(140,220,255,.15)" transform="rotate(6,155,110)"/>
        <ellipse cx="70"  cy="148" rx="32" ry="18" fill="rgba(140,220,255,.11)" transform="rotate(-10,70,148)"/>
        <ellipse cx="268" cy="128" rx="36" ry="20" fill="rgba(140,220,255,.12)" transform="rotate(18,268,128)"/>
        <ellipse cx="200" cy="170" rx="34" ry="18" fill="rgba(140,220,255,.11)" transform="rotate(-6,200,170)"/>
        <ellipse cx="105" cy="175" rx="26" ry="14" fill="rgba(140,220,255,.1)"  transform="rotate(12,105,175)"/>
        <ellipse cx="290" cy="70"  rx="22" ry="13" fill="rgba(140,220,255,.1)"  transform="rotate(-18,290,70)"/>
      </g>

      {/* ── Surface wave lines ── */}
      <g clipPath="url(#pi-c)" fill="none" strokeLinecap="round">
        <path d="M20 44  C72 37  127 52  175 44  C222 36 272 51 320 44" stroke="rgba(255,255,255,.26)" strokeWidth="2"/>
        <path d="M20 70  C70 63  126 78  174 70  C220 62 270 77 320 70" stroke="rgba(255,255,255,.17)" strokeWidth="1.4"/>
        <path d="M20 98  C72 91  127 106 175 98  C222 90 271 105 320 98" stroke="rgba(255,255,255,.22)" strokeWidth="1.8"/>
        <path d="M20 126 C71 119 127 134 175 126 C222 118 271 133 320 126" stroke="rgba(255,255,255,.16)" strokeWidth="1.3"/>
        <path d="M20 154 C72 147 127 162 175 154 C222 146 271 161 320 154" stroke="rgba(255,255,255,.2)" strokeWidth="1.7"/>
        <path d="M20 182 C70 175 126 190 174 182 C220 174 271 189 320 182" stroke="rgba(255,255,255,.13)" strokeWidth="1.2"/>
      </g>

      {/* Diagonal shimmer band */}
      <rect x="20" y="20" width="300" height="190" rx="13" fill="url(#pi-sh)"/>

      {/* ── Water surface sparkles ── */}
      <g clipPath="url(#pi-c)" opacity=".6">
        {[[38,30],[105,26],[192,34],[276,28],[68,22],[150,25],[235,22],[88,38],[178,30],[255,36],[120,22],[302,24]].map(([x,y],i) => (
          <circle key={i} cx={x} cy={y} r={i%4===0?1.9:i%3===0?1.4:1.1} fill="white"/>
        ))}
      </g>

      {/* ── Lane dividers — 3 ropes, 4 lanes — */}
      {[95, 170, 245].map((x, li) => (
        <g key={`r${li}`} clipPath="url(#pi-c)">
          <line x1={x} y1="20" x2={x} y2="210" stroke="url(#pi-rl)" strokeWidth=".9"/>
          {Array.from({length:8}, (_, j) => {
            const cy  = 28 + j * 26
            const yel = (j + li) % 2 === 0
            return (
              <g key={j}>
                <ellipse cx={x}   cy={cy}     rx="5.5" ry="3.5"
                  fill={yel ? '#B87A0A' : '#B8B8C2'} opacity=".88"/>
                <ellipse cx={x-1.3} cy={cy-1} rx="3.2" ry="1.8"
                  fill={yel ? 'rgba(255,195,50,.48)' : 'rgba(255,255,255,.48)'}/>
                <ellipse cx={x}   cy={cy}     rx="5.5" ry="3.5"
                  fill="none" stroke={yel ? 'rgba(230,160,30,.5)' : 'rgba(255,255,255,.35)'} strokeWidth=".5"/>
              </g>
            )
          })}
        </g>
      ))}

      {/* ── Pool stairs — bottom-left ── */}
      <g opacity=".82">
        <rect x="20" y="192" width="38" height="10" rx="3"
          fill="#0A3048" stroke="rgba(140,215,240,.28)" strokeWidth=".8"/>
        <rect x="20" y="182" width="29" height="10" rx="2.5"
          fill="#0C3A58" stroke="rgba(140,215,240,.22)" strokeWidth=".7"/>
        <rect x="20" y="172" width="20" height="10" rx="2"
          fill="#0E4265" stroke="rgba(140,215,240,.17)" strokeWidth=".6"/>
        <line x1="20" y1="192" x2="58"  y2="192" stroke="rgba(255,255,255,.32)" strokeWidth=".9"/>
        <line x1="20" y1="182" x2="49"  y2="182" stroke="rgba(255,255,255,.24)" strokeWidth=".7"/>
        <line x1="20" y1="172" x2="40"  y2="172" stroke="rgba(255,255,255,.18)" strokeWidth=".6"/>
      </g>

      {/* ── Starting blocks — top edge (4 blocks) ── */}
      {[47, 122, 197, 272].map((x, i) => (
        <g key={`sb${i}`} opacity=".74">
          <rect x={x-11} y="20" width="22" height="12" rx="2.5"
            fill="#162E42" stroke="rgba(140,215,240,.24)" strokeWidth=".7"/>
          <rect x={x-9}  y="18" width="18" height="5"  rx="1.5"
            fill="#1E4862" stroke="rgba(140,215,240,.32)" strokeWidth=".6"/>
          <line x1={x-7} y1="19.5" x2={x+7} y2="19.5" stroke="rgba(255,255,255,.28)" strokeWidth=".55"/>
          <line x1={x-7} y1="21.5" x2={x+7} y2="21.5" stroke="rgba(255,255,255,.18)" strokeWidth=".4"/>
        </g>
      ))}

      {/* ── Pool ladder — right side, near bottom ── */}
      <g opacity=".7">
        <line x1="308" y1="168" x2="316" y2="172" stroke="rgba(180,215,235,.5)" strokeWidth="1.4"/>
        <line x1="308" y1="183" x2="316" y2="187" stroke="rgba(180,215,235,.5)" strokeWidth="1.4"/>
        {[175, 181, 187, 193, 199, 205].map(y => (
          <line key={y} x1="308" y1={y} x2="316" y2={y+2}
            stroke="rgba(200,228,242,.48)" strokeWidth="1.1"/>
        ))}
      </g>

      {/* ── Lane numbers (deck top & bottom) ── */}
      {[1,2,3,4].map((n,i) => {
        const cx = 47 + i*75
        return (
          <g key={`ln${n}`}>
            <text x={cx} y="13.5" textAnchor="middle" fontSize="8.5" fontWeight="700" letterSpacing="1"
              fill="rgba(130,195,220,.28)" fontFamily="'JetBrains Mono',monospace">{n}</text>
            <text x={cx} y="225"  textAnchor="middle" fontSize="8.5" fontWeight="700" letterSpacing="1"
              fill="rgba(130,195,220,.22)" fontFamily="'JetBrains Mono',monospace">{n}</text>
          </g>
        )
      })}

      {/* ── Depth markers on left deck ── */}
      <text x="13" y="80"  textAnchor="middle" fontSize="7.5" fill="rgba(130,195,220,.3)"
        fontFamily="'JetBrains Mono',monospace" transform="rotate(-90,13,80)">1.2 m</text>
      <text x="13" y="172" textAnchor="middle" fontSize="7.5" fill="rgba(130,195,220,.22)"
        fontFamily="'JetBrains Mono',monospace" transform="rotate(-90,13,172)">2.0 m</text>
      <line x1="20" y1="70"  x2="27" y2="70"  stroke="rgba(130,195,220,.28)" strokeWidth=".9"/>
      <line x1="20" y1="162" x2="27" y2="162" stroke="rgba(130,195,220,.2)"  strokeWidth=".9"/>

      {/* Pool rim inner border highlight */}
      <rect x="20" y="20" width="300" height="190" rx="13"
        fill="none" stroke="rgba(20,180,200,.22)" strokeWidth="1"/>
    </svg>
  )
}

/* ── EmptyState ── */
export function EmptyState({ title, description, action, actionLabel, c }) {
  const dk = c === DK
  return (
    <div style={{
      display:'flex', flexDirection:'column', alignItems:'center',
      justifyContent:'center', minHeight:440, textAlign:'center', padding:'36px 24px 52px',
    }}>
      {/* Pool illustration wrapper */}
      <div style={{ position:'relative', width:'100%', maxWidth:340, marginBottom:30 }}>
        {/* Wide diffuse glow underneath */}
        <div style={{
          position:'absolute', left:'-10%', right:'-10%', top:'15%', bottom:'-15%',
          background:'radial-gradient(ellipse at 50% 65%, rgba(8,108,150,.32) 0%, transparent 72%)',
          filter:'blur(24px)', pointerEvents:'none',
        }}/>
        {/* Subtle top glow */}
        <div style={{
          position:'absolute', left:'10%', right:'10%', top:'-10%', height:'40%',
          background:'radial-gradient(ellipse at 50% 0%, rgba(31,200,214,.14) 0%, transparent 70%)',
          filter:'blur(12px)', pointerEvents:'none',
        }}/>
        {/* Pool container */}
        <div style={{
          borderRadius:24, overflow:'hidden', position:'relative',
          border:`1px solid ${dk ? 'rgba(20,185,210,.22)' : 'rgba(10,123,134,.2)'}`,
          boxShadow: dk
            ? '0 2px 0 rgba(255,255,255,.06) inset, 0 8px 48px rgba(0,8,20,.65), 0 0 0 1px rgba(15,163,177,.08)'
            : '0 8px 40px rgba(8,90,130,.16), 0 2px 0 rgba(255,255,255,.8) inset',
        }}>
          <PoolIllustration/>
        </div>
      </div>

      <h3 style={{ fontSize:20, fontWeight:900, color:c.white, marginBottom:10, letterSpacing:'-.03em' }}>{title}</h3>
      <p style={{ fontSize:14, color:c.muted, maxWidth:300, lineHeight:1.78, marginBottom:30 }}>{description}</p>

      {action && (
        <button onClick={action} style={{
          padding:'12px 36px', borderRadius:999, border:'none',
          background:'linear-gradient(135deg,#1FC8D6 0%,#0FA3B1 50%,#0A7B86 100%)',
          color:'#fff', fontSize:14, fontWeight:700, cursor:'pointer',
          fontFamily:"'Plus Jakarta Sans','Manrope',sans-serif",
          boxShadow:'0 4px 24px rgba(15,163,177,.45), 0 1px 0 rgba(255,255,255,.2) inset',
          transition:'all .22s', letterSpacing:'-.01em',
        }}
        onMouseEnter={e => { e.currentTarget.style.boxShadow='0 8px 36px rgba(15,163,177,.65), 0 1px 0 rgba(255,255,255,.2) inset'; e.currentTarget.style.transform='translateY(-2px)' }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow='0 4px 24px rgba(15,163,177,.45), 0 1px 0 rgba(255,255,255,.2) inset'; e.currentTarget.style.transform='none' }}>
          {actionLabel}
        </button>
      )}
    </div>
  )
}
